import yaml from 'js-yaml';
import { DIVERSION_YAML_SEPARATOR, NOTICE_BODY_MAX_LENGTH } from 'constants/Values';

// The diversion body is COMPOSED, and the operator only writes part of it.
//
// What goes on the wire is `<preamble>\n---\n<yaml>`, where the YAML carries every safe
// zone's name, coordinates, address and placeId. `DiversionSubform` used to own this and
// nothing measured the result: `NotificationForm.isSubmitingDisabled` returns early for
// DIVERSION, so the `body.length > NOTICE_BODY_MAX_LENGTH` check that guards every other
// notice type never ran on the one type whose body the operator cannot see.
//
// The bound is real. `POST /core/notifications/filters` takes a `FilteredNotificationRequest`
// whose `template` is a `NotificationTemplateRequest` marked `@Valid`, so its
// `@Size(min = 1, max = 1000)` cascades onto the composed string. Exceeding it is a 400 at
// send time, on the safety-critical flow, with nothing on screen to explain it.
//
// Measured cost of the YAML, with realistic shelters (~180 chars each):
//
//     shelters   yaml+separator   left for the preamble (of 999)
//            1              200                              799
//            3              572                              427
//            5              928                               71
//            6             1097          negative — unsendable empty
//
// So the ceiling is five safe zones, and the sixth cannot be sent whatever is typed. That is
// a product limit, not a bug to code around: the storage behind it is unbounded
// (`Notification.body` is `@Type(type = "text")`), so raising it is a decision about the
// contract rather than about the database. Until that decision is made, the operator is told.
//
// This lives apart from the component so the ceiling logic above is testable directly,
// without needing to render a subform. `DiversionSubform.serializeBody` delegates here
// and keeps its own signature, so no call site changed.

/**
 * Compose the body `POST /core/notifications/filters` expects:
 * `<preamble><DIVERSION_YAML_SEPARATOR>\n<yamlText>`.
 *
 * The separator gives the server's `DiversionPayloadParser` its split point at
 * `body.indexOf('\n---')`, with the YAML then starting at the `---` doc-start marker
 * SnakeYAML ignores.
 *
 * @param {{preamble?: string, shelters?: Array, policy?: string, cancel?: boolean}} value
 * @param {string} [notificationId] Path A threading — the banner-prefilled cancellation.
 * @returns {string}
 */
export function composeDiversionBody(value, notificationId) {
  const { preamble, shelters, policy, cancel } = value;

  let payload;
  if (cancel) {
    payload = { cancel: true };
    if (notificationId) {
      payload.notificationId = notificationId;
    }
  } else {
    // Strip undefined keys explicitly: js-yaml v3 throws on undefined-valued properties, and
    // a shelter's optional fields are legitimately absent.
    const cleanShelters = (shelters || []).map(s => {
      const out = { location: s.location };
      if (s.coords !== undefined) out.coords = s.coords;
      if (s.address !== undefined) out.address = s.address;
      if (s.placeId !== undefined) out.placeId = s.placeId;
      return out;
    });
    payload = { policy, shelters: cleanShelters };
  }

  return `${preamble || ''}${DIVERSION_YAML_SEPARATOR}\n${yaml.safeDump(payload)}`;
}

/**
 * What the composed body costs, and what the operator has left to type.
 *
 * `overhead` is everything the operator did not write — separator plus YAML — so it grows as
 * safe zones are added and SHRINKS the allowance without the preamble changing. That is the
 * whole reason this is surfaced: otherwise the character count moves on its own and the send
 * fails for a reason nothing on screen explains.
 *
 * `remaining` can be negative, and deliberately is not clamped: at six safe zones there is no
 * allowance left at all, and rounding that up to zero would hide the only fact worth saying.
 *
 * @param {Object} value the subform value.
 * @param {string} [notificationId]
 * @param {number} [max] defaults to `NOTICE_BODY_MAX_LENGTH` (999, one tighter than the
 *   server's 1000 — keep it that way so the client refuses first).
 * @returns {{composed: number, overhead: number, preamble: number, remaining: number, over: boolean}}
 */
export function diversionBodyBudget(value, notificationId, max = NOTICE_BODY_MAX_LENGTH) {
  const composed = composeDiversionBody(value, notificationId).length;
  const preamble = (value && value.preamble) || '';
  return {
    composed,
    overhead: composed - preamble.length,
    preamble: preamble.length,
    remaining: max - composed,
    over: composed > max,
  };
}
